/**
 * Leitura das respostas do servidor durante a emissão — porta
 * `cteatf_respostas.dart`. Recebe status e corpo, devolve o veredito; quem
 * aplica o efeito (descartar chave, abrir tela) é o chamador.
 */

export type DesfechoSefaz = 'autorizado' | 'recusado' | 'indeterminado';

/**
 * Lê a resposta do `POST /cteos/:id/enviar`. Corpo ilegível é indeterminado,
 * NUNCA recusado — sem conseguir ler a resposta não se sabe o estado do
 * documento no servidor, e tratar como recusa dispararia rollback de um
 * CT-e possivelmente válido.
 */
export function classificarSefaz(status: number, corpo: any): { desfecho: DesfechoSefaz; mensagem: string } {
  if (corpo == null || typeof corpo !== 'object') {
    return { desfecho: 'indeterminado', mensagem: `Erro ao autorizar na SEFAZ (HTTP ${status}).` };
  }
  if (status === 200 && corpo.sucesso === true) {
    return { desfecho: 'autorizado', mensagem: '' };
  }
  return { desfecho: 'recusado', mensagem: `Recusado pela SEFAZ: ${corpo.motivoRetorno ?? corpo.error ?? 'erro desconhecido'}` };
}

export type ErroVeiculo = 'naoInformado' | 'naoEncontrado' | 'semRegistro' | 'proprietarioIncompleto' | 'outro';

/** Classifica a recusa de veículo do POST /cteos. `null` = não é uma. */
export function classificarErroVeiculo(status: number, corpo: any): { tipo: ErroVeiculo; erroApi: string | null } | null {
  if (status < 400 || status >= 500) return null;
  if (corpo == null || typeof corpo !== 'object') return null;

  const codigo = corpo.codigoRetorno != null ? String(corpo.codigoRetorno) : '';
  const erroApi = corpo.error != null ? String(corpo.error) : null;

  // 422 sem codigoRetorno (verificado em produção 07/08/2026) — validação de
  // payload anterior aos códigos CTEOS_*, equivale a "não informado".
  const semVeiculoNoPayload = codigo === '' && (erroApi ?? '').toLowerCase().includes('veículo é obrigatório');
  if (!codigo.startsWith('CTEOS_') && !semVeiculoNoPayload) return null;

  const chave = semVeiculoNoPayload ? 'CTEOS_VEICULO_NAO_INFORMADO' : codigo;
  const tipo: ErroVeiculo =
    chave === 'CTEOS_VEICULO_NAO_INFORMADO' ? 'naoInformado' :
    chave === 'CTEOS_VEICULO_NAO_ENCONTRADO' ? 'naoEncontrado' :
    chave === 'CTEOS_VEICULO_SEM_REGISTRO' ? 'semRegistro' :
    chave === 'CTEOS_PROPRIETARIO_INCOMPLETO' ? 'proprietarioIncompleto' : 'outro';

  return { tipo, erroApi };
}

/** Só falta de TAF/Registro é corrigível pelo cooperado — o resto depende da cooperativa. */
export function erroVeiculoCorrigivelNoApp(tipo: ErroVeiculo): boolean {
  return tipo === 'semRegistro';
}

export type AcaoCredito = 'bloquear' | 'ofertarCobertura' | 'renovarCobertura' | 'avisar';

export interface VereditoCredito {
  acao: AcaoCredito;
  mensagem: string;
  corpo: Record<string, unknown>;
  avisoTitulo?: string;
}

const CREDITO_SALDO_INSUFICIENTE = 'CREDITO_SALDO_INSUFICIENTE';
const CREDITO_EMISSAO_EM_CURSO = 'CREDITO_EMISSAO_EM_CURSO';
const CREDITO_ROTA_INDISPONIVEL = 'CREDITO_ROTA_INDISPONIVEL';
const CREDITO_MODULO_INDISPONIVEL = 'CREDITO_MODULO_INDISPONIVEL';
const CREDITO_ALIQUOTA_INDISPONIVEL = 'CREDITO_ALIQUOTA_INDISPONIVEL';
const CREDITO_COBERTURA_NAO_LIBERADA = 'CREDITO_COBERTURA_NAO_LIBERADA';
const CREDITO_COBERTURA_INVALIDA = 'CREDITO_COBERTURA_INVALIDA';

/**
 * Lê as recusas por crédito do POST /cteos e do POST /atf/solicitar. `null`
 * para o pós-pago sempre — porta que mantém o fluxo dele idêntico ao de
 * antes da 0004.
 */
export function classificarRecusaDeCredito({
  status, corpo, exibeCredito, podeCobrirPorPix,
}: { status: number; corpo: any; exibeCredito: boolean; podeCobrirPorPix: boolean }): VereditoCredito | null {
  if (!exibeCredito) return null;
  if (![402, 403, 409, 503].includes(status)) return null;
  if (corpo == null || typeof corpo !== 'object') return null;

  const codigo = corpo.codigo != null ? String(corpo.codigo) : '';
  const msg = corpo.error != null ? String(corpo.error) : '';
  if (!msg) return null;

  const v = (acao: AcaoCredito, avisoTitulo?: string): VereditoCredito => ({ acao, mensagem: msg, corpo, avisoTitulo });

  if (codigo === CREDITO_SALDO_INSUFICIENTE) {
    // Quem pode cobrir por Pix NÃO é bloqueado — a recusa vira o gatilho
    // tardio da folha de pagamento.
    return v(podeCobrirPorPix ? 'ofertarCobertura' : 'bloquear');
  }
  if (codigo === CREDITO_MODULO_INDISPONIVEL) return v('avisar', 'Emissão indisponível');
  if (codigo === CREDITO_ALIQUOTA_INDISPONIVEL) return v('avisar', 'Emissão indisponível');
  if (codigo === CREDITO_COBERTURA_NAO_LIBERADA || codigo === CREDITO_COBERTURA_INVALIDA) return v('renovarCobertura');
  if (codigo === CREDITO_EMISSAO_EM_CURSO) return v('avisar', 'Emissão em andamento');
  if (codigo === CREDITO_ROTA_INDISPONIVEL) return v('avisar', 'Distância indisponível');
  return null;
}

/** Mensagem de erro do corpo, ou o fallback quando ele não traz nenhuma. */
export function mensagemDoBody(corpo: any, fallback: string): string {
  if (corpo == null || typeof corpo !== 'object') return fallback;
  return String(corpo.message ?? corpo.error ?? fallback);
}

/** Traduz falhas de transporte para o que o cooperado pode fazer a respeito. */
export function sanitizarErro(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (msg.includes('Network Error') || msg.includes('Failed to fetch')) {
    return 'Sem conexão com a internet. Verifique sua rede e tente novamente.';
  }
  if (msg.includes('timeout') || msg.includes('ECONNABORTED')) {
    return 'Tempo esgotado. Tente novamente.';
  }
  return msg;
}
